package info.rezident.app.ui.components.sections

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.css.AnimationIterationCount
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.functions.blur
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.components.graphics.Image
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.animation.Keyframes
import com.varabyte.kobweb.silk.style.animation.toAnimation
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import info.rezident.app.MaxWidthStyle
import info.rezident.app.ui.components.widgets.Spacer
import info.rezident.app.ui.theme.AppTheme
import org.jetbrains.compose.web.css.*

val quizHeaderHeightStyle = CssStyle {
    base {
        Modifier.height(70.px)
    }

    Breakpoint.SM {
        Modifier.height(75.px)
    }

    Breakpoint.MD {
        Modifier.height(90.px)
    }

    Breakpoint.LG {
        Modifier.height(110.px)
    }
}

// Define a pulse animation for the progress bar
val progressPulseKeyframes = Keyframes {
    0.percent {
        Modifier.backgroundColor(rgb(33, 158, 188)).opacity(70.percent)
    }
    50.percent {
        Modifier.backgroundColor(rgb(142, 202, 230)).opacity(100.percent)
    }
    100.percent {
        Modifier.backgroundColor(rgb(33, 158, 188)).opacity(70.percent)
    }
}

@Composable
fun QuizHeader(
    currentStep: Int,
    totalSteps: Int,
    nextStepTitle: String?,
    modifier: Modifier = Modifier,
) {
    Box(
        modifier
            .fillMaxWidth()
            .backgroundColor(AppTheme.colors.header)
            .backdropFilter(blur(4.px))
            .zIndex(1000)
            .position(Position.Fixed)
            .left(0.percent)
            .top(0.percent)
            .right(0.percent)
            .styleModifier {
                property("margin", "auto")
            },
        contentAlignment = Alignment.TopCenter
    ) {
        Column(
            MaxWidthStyle.toModifier()
                .then(headerPaddingStyle.toModifier())
                .fillMaxWidth(),
            horizontalAlignment = Alignment.CenterHorizontally
        ) {
            // Logo and next section row
            Row(
                Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.CenterVertically
            ) {
                // Logo
                val colorMode = ColorMode.current
                Image(
                    if (colorMode.isLight) {
                        "/logo-light.png"
                    } else {
                        "/logo-dark.png"
                    },
                    "rezident.info Logo",
                    headerLogoStyle.toModifier()
                        .display(DisplayStyle.Block)
                )

                if (nextStepTitle != null) {
                    Column {
                        SpanText(
                            text = "Urmează",
                            modifier = Modifier
                                .color(AppTheme.colors.text)
                                .fontSize(16.px)
                                .fontWeight(400)
                        )

                        SpanText(
                            text = nextStepTitle,
                            modifier = Modifier
                                .color(AppTheme.colors.text)
                                .fontSize(16.px)
                                .fontWeight(FontWeight.Bold)
                        )
                    }
                }
            }

            Spacer(Modifier.height(10.px))

            val progressValue = (currentStep * 100 / totalSteps).percent
            // Progress bar
            Box(
                modifier = Modifier
                    .fillMaxWidth()
                    .height(8.px)
                    .backgroundColor(Colors.LightGray)
                    .borderRadius(AppTheme.shapes.bigRound)
            ) {
                // Use key to force recomposition and restart animation
                Box(
                    modifier = Modifier
                        .fillMaxHeight()
                        .width(progressValue)
                        .backgroundColor(rgb(33, 158, 188)) // Blue Green from brand colors
                        .borderRadius(
                            topLeft = AppTheme.shapes.bigRound,
                            bottomLeft = AppTheme.shapes.bigRound,
                            topRight = if (progressValue == 100.percent) AppTheme.shapes.bigRound else 0.px,
                            bottomRight = if (progressValue == 100.percent) AppTheme.shapes.bigRound else 0.px,
                        )
                        .animation(
                            progressPulseKeyframes.toAnimation(
                                duration = 300.ms,
                                iterationCount = AnimationIterationCount.of(1)
                            )
                        )
                        .styleModifier {
                            property("transition", "width 0.3s ease-in-out")
                        }
                )
            }
        }
    }
}

@Composable
fun QuizHeaderSpacer() {
    Spacer(
        quizHeaderHeightStyle
            .toModifier()
            .margin(bottom = 20.px)
    )
}